<template>
  <Transition v-if="open" :name="!isMobile? 'slide-fade' : 'slidein'">
    <div class="ttt-date-picker" :class="{'inline': inline, 'visible': open}" @click.prevent.stop="closeSubComponents">
      <div :style="[getWidth]" class="date-picker-container">
        <div v-if="isMobile && showMobileInput" class="mobile-input-container">
          <div class="mobile-input-wrapper">
            <div class="back-btn-container">
              <button
                type="button"
                tabindex="0"
                class="back-btn"
                @click="close"
              >
                Back
              </button>
            </div>

            <div v-if="range" class="mobile-input">
              <div><span :class="{'bold-text' : value && value.startDate}">{{ displayStartDate }}</span> - <span :class="{'bold-text' : value && value.endDate}">{{ displayEndDate }}</span></div>
              <Cross v-if="value && value.startDate" class="mobile-input__icon" alt="clear" @click="clearDates" />
              <div v-else class="mobile-input__icon calendar" alt="calendar" />
            </div>

            <div v-if="!range" class="mobile-input">
              <div><span :class="{'bold-text' : value }">{{ displaySingleDate }}</span></div>
              <Cross v-if="value && value.startDate" class="mobile-input__icon" alt="clear" @click="clearDates" />
              <div v-else class="mobile-input__icon calendar" alt="calendar" />
            </div>
          </div>
          <WeekDays v-if="switchWeekdays" :week-days="weekDays" :fontSize="fontSize" class="mobile-input-weekdays" />
          <div class="mobile-input-container__border" />
        </div>

        <div id="monthsContainer" ref="monthsContainer" class="months-container" :class="getLayoutClass">
          <div v-for="(dp, index) in getDatePickerMonths" :id="index" :key="index" class="date-picker-month">
            <DatePicker
              v-if="dp.show"
              :id="dp.id"
              :key="dp.id"
              :ref="`dp_${index}`"
              v-model="date"
              :index="index"
              :active="dp.active"
              :visible="dp.show"
              :month="dp.month"
              :showPrev="dp.showPrev"
              :showNext="dp.showNext"
              :highlight="highlight"
              :transitionDirection="transitionDirection"
              :range="range"
              :dynamic="dynamic"
              :highlightOnMouseover="highlightOnMouseover"
              :allowTooltips="allowTooltips"
              :lazyLoadMonths="lazyLoadMonths"
              :switchWeekdays="switchWeekdays"
              :first-day-of-week="firstDayOfWeek"
              :availableDates="availableDates"
              :availableCheckInDates="availableCheckInDates"
              :availableCheckOutDates="availableCheckOutDates"
              :availableDatesWeekly="availableDatesWeekly"
              :pendingDates="pendingDates"
              :pendingDatesWeekly="pendingDatesWeekly"
              :disabled-dates="disabledDates"
              :disabled-weekly="disabledWeekly"
              :disablePastDates="disablePastDates"
              :no-weekends-days="noWeekendsDays"
              :min-date="minDate"
              :max-date="maxDate"
              :minBooking="minBooking"
              :maxBooking="maxBooking"
              :messages="messages"
              :inline="inline"
              :locale="locale"
              :containerWidth="containerWidth"
              :fixedHeight="fixedHeight"
              :monthInTransition="monthInTransition"
              @change-month="changeMonth"
              @change-year-month="changeYearMonth"
              @change-year="changeYear($event, index)"
              @close="close"
              @selection="$emit('selection', $event)"
              @fontSize="fontSize = $event"
              @mousemode="onMouseModeChanged"
              @closeYearMonthSelector="closeSubComponents"
            />
          </div>

          <div v-if="lazyLoadMonths && isMobile && amountOfMonths <= maxMonths" id="invisible-load-more" class="load-more">
            <button
              aria-label="Load more"
              type="button"
              tabindex="0"
              class="load-more__button btn btn-underlined-dark-sm"
              @click.prevent.stop="loadNewMonth"
            >
              Load More
            </button>
          </div>
        </div>

        <div v-if="showKeys || bookingInfoText || showControls" class="bottom-container">
          <div v-if="showKeys || bookingInfoText || (isMobile && showControls && !showMobileInput)" class="bottom-container__border" />

          <div v-if="showKeys && showInfo && isMobile" @click.prevent.stop="toggleInfoOpen">
            <div v-show="!infoOpen" class="calendar-info-closed">
              <div class="calendar-info-closed__line" />
              <div class="calendar-info-closed__text">
                Click for calendar keys
              </div>
            </div>
            <div v-show="infoOpen">
              <img alt="close" src="../../assets/img/src/icons/scroll-down-arrow-icon.svg" class="down-icon lazyload">
              <div v-if="showKeys" class="calendar-keys">
                <div class="calendar-keyset">
                  <div class="c-key available-date">
                    16
                  </div>
                  <div><span style="white-space: nowrap">Check-in</span> / <span style="white-space: nowrap">Check-out</span></div>
                </div>
                <div class="calendar-keyset">
                  <div class="c-key contact-date">
                    23
                  </div>
                  <div>Contact us for availability</div>
                </div>
              </div>
              <div v-if="bookingInfoText" class="booking-information" v-html="bookingInfoText" />
            </div>
          </div>

          <div v-if="showInfo && !isMobile">
            <div v-if="showKeys" class="calendar-keys">
              <div class="calendar-keyset">
                <div class="c-key available-date">
                  16
                </div>
                <div><span style="white-space: nowrap">Check-in</span> / <span style="white-space: nowrap">Check-out</span></div>
              </div>
              <div class="calendar-keyset">
                <div class="c-key contact-date">
                  23
                </div>
                <div>Contact us for availability</div>
              </div>
            </div>
            <div v-if="bookingInfoText" class="booking-information" v-html="bookingInfoText" />
          </div>

          <div v-if="showButtonSet && buttonSetActive" class="button-controls-container">
            <button
              type="button"
              tabindex="0"
              :class="range ? 'btn-underlined-dark-sm' : 'btn-underlined-dark-xs'"
              @click="clearDates"
            >
              Clear <span v-if="isDesktop">{{ range ? 'dates' : 'date' }}</span>
            </button>

            <button
              v-if="!showOnlyClear"
              type="button"
              tabindex="0"
              :class="range ? 'btn-primary-dark-sm' : 'btn-primary-dark-xs'"
              @click="close"
            >
              Confirm {{ range ? 'dates' : 'date' }}
            </button>
          </div>

          <div v-if="!showButtonSet && (showControls && !isMobile) || (showControls && isMobile && !showMobileInput)" class="controls-container">
            <button
              type="button"
              tabindex="0"
              class="controls-btn btn btn-underlined-dark-sm"
              @click="clearDates"
            >
              Clear {{ range ? 'dates' : 'date' }}
            </button>

            <button
              type="button"
              tabindex="0"
              class="controls-btn btn btn-primary-dark-sm"
              @click="close"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import Vue from 'vue';
import { _ } from 'vue-underscore';
import moment from 'moment';
import DatePicker from './DatePicker';
import Month, { getWeekDays } from './modules/month';
import responsive from './mixins/responsive';
import WeekDays from './ui/WeekDays';
import Cross from '../icons/Cross.vue';

const updateMomentLocale = (locale, firstDayOfWeek) => {
  moment.locale(locale);
  if (firstDayOfWeek) {
    const firstDayNumber = Number.isInteger(firstDayOfWeek) && firstDayOfWeek === 0
      ? 7
      : firstDayOfWeek;
      // : firstDayOfWeek || moment.localeData(locale).firstDayOfWeek();
    moment.updateLocale(locale, { week: { dow: firstDayNumber } });
  }
};

export default {
  name: 'TTTDatePicker',
  components: {
    DatePicker,
    WeekDays,
    Cross,
  },
  mixins: [responsive],
  inheritAttrs: false,
  props: {
    visible: { type: Boolean, required: true, default: false },
    double: { type: Boolean, default: false },
    showControls: { type: Boolean, default: true },
    showMobileInput: { type: Boolean, default: false },
    hideArrowsOnMobile: { type: Boolean, default: false },
    limitDoubleNav: { type: Boolean, default: false },
    showKeys: { type: Boolean, default: false },
    bookingInfoText: { type: String, default: null },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (propValue) => {
        const listOfAvailableModes = ['horizontal', 'vertical'];
        const validMode = listOfAvailableModes.some((m) => propValue === m);
        return validMode;
      },
    },
    numberOfMonths: { type: Number, default: 0 },
    maxMonths: { type: Number, default: 20 },
    maxContainerWidth: { type: Number, default: 1400 },
    containerWidth: { type: String, default: '284px' },
    containerSpacing: { type: Number, default: 30 },
    showButtonSet: { type: Boolean, default: false },
    showOnlyClear: { type: Boolean, default: false },

    // date picker month props
    value: { type: [String, Object], default: null },
    range: { type: Boolean, default: null },
    dynamic: { type: Boolean, default: true },
    highlightOnMouseover: { type: Boolean, default: false },
    allowTooltips: { type: Boolean, default: true },
    lazyLoadMonths: { type: Boolean, default: false },
    switchWeekdays: { type: Boolean, default: false },
    firstDayOfWeek: { type: Number, default: 1 },
    availableDates: { type: Array },
    availableCheckInDates: { type: Array },
    availableCheckOutDates: { type: Array },
    availableDatesWeekly: { type: Array },
    pendingDates: { type: Array },
    pendingDatesWeekly: { type: Array },
    disabledDates: { type: Array },
    disabledWeekly: { type: Array },
    disablePastDates: { type: Boolean, default: false },
    noWeekendsDays: { type: Boolean },
    maxDate: { type: String, default: null },
    minDate: { type: String, default: null },
    minBooking: { type: Number },
    maxBooking: { type: Number },
    messages: { type: Object },
    fixedHeight: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    locale: { type: String, default: '' },
    withinModal: { type: Boolean, default: false },
  },
  data() {
    return {
      month: this.getMonth(),
      transitionName: 'slidevnext',
      transitionDirection: 'next',
      highlight: false,
      pickerMonths: [],
      amountOfMonths: 1,
      monthsObserver: null,
      fontSize: '15px',
      open: false,
      buttonSetActive: false,
      showInfo: true,
      infoOpen: false,
      infoNavOpen: true,
      monthInTransition: false,
      scrollTimer: -1,
    };
  },
  computed: {
    displaySingleDate() {
      return this.value ? moment(this.value, 'YYYY-MM-DD HH:mm').format('D MMMM YYYY') : 'Date';
    },
    displayStartDate() {
      return this.value && this.value.startDate ? this.value.startDate : 'Add dates';
    },
    displayEndDate() {
      return this.value && this.value.endDate ? this.value.endDate : '';
    },
    getDatePickerMonths() {
      return this.pickerMonths;
    },
    weekDays() {
      return getWeekDays(this.locale, this.firstDayOfWeek);
    },
    getLayoutClass() {
      return {
        single: !this.double,
        'double-horizontal': this.double && this.layout === 'horizontal',
        'double-vertical': this.double && this.layout === 'vertical',
      };
    },
    getWidth() {
      // to do - add for single picker too after testing
      const space = this.inline && this.layout === 'horizontal'
        ? 10
        : this.inline && this.layout === 'vertical'
          ? 0
          : !this.inline && !this.double
            ? 22
            : 30;

      const pickerWidth = parseInt(this.containerWidth) + space;
      const maxWidth = parseInt(this.maxContainerWidth);
      const estimatedWidth = this.amountOfMonths * pickerWidth;
      const maxPerRow = Math.round(maxWidth / pickerWidth);
      const monthsPerRow = estimatedWidth > maxWidth ? maxPerRow : this.amountOfMonths;
      const size = this.isMobile
        ? '100%'
        : this.double && this.layout === 'horizontal' && !this.isMobile
          ? `${monthsPerRow * pickerWidth}px`
          : `${pickerWidth}px`;
      return {
        width: size,
        maxWidth: size,
        // minWidth: size,
      };
    },
    date: {
      set(value) {
        this.emitValue({
          value,
          type: 'date',
        });
      },
      get() {
        const date = this.value
          ? this.range
            ? {
              start: this.value.start ? moment(this.value.start).format('YYYY-MM-DD') : null,
              end: this.value.end ? moment(this.value.end).format('YYYY-MM-DD') : null,
            }
            : moment(this.value, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD')
          : this.range
            ? { start: null, end: null }
            : null;
        return date;
      },
    },
  },
  watch: {
    month() {
      this.checkLayout();
    },
    value: {
      handler(value) {
        if (this.range) {
          this.buttonSetActive = value && value.startDate && value.endDate;
        } else {
          this.buttonSetActive = !!value;
        }

        if (this.showButtonSet) {
          this.showInfo = !this.buttonSetActive;
        }
      },
      immediate: true,
      deep: true,
    },
    locale(value) {
      updateMomentLocale(value, this.firstDayOfWeek);
      this.month = this.getMonth();

      if (this.value && this.value.start) {
        this.value.startDate = moment(this.value.start).format('D MMM YYYY');
      }
      if (this.value && this.value.end) {
        this.value.endDate = moment(this.value.end).format('D MMM YYYY');
      }
    },
    visible: {
      handler(val) {
        if (this.isMobile) this.$store.state.common.datepickerOpen = val;
        this.open = val;
        if (val) {
          this.month = this.getMonth();
          this.isMobile && !this.inline ? this.hideBodyScroll(true) : this.hideBodyScroll(false);
          this.$nextTick(() => { document.getElementById('monthsContainer').addEventListener('scroll', this.handleScroll, { passive: true }); });
        }
        else {
          this.hideBodyScroll(false);
          if (document.getElementById('monthsContainer')) {
            document.getElementById('monthsContainer').removeEventListener('scroll', this.handleScroll);
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    updateMomentLocale(this.locale, this.firstDayOfWeek);
  },
  mounted() {
    this.checkLayout();
    this.updateValue();
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMouseMove);
    if (document.getElementById('monthsContainer')) {
      document.getElementById('monthsContainer').removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    toggleInfoOpen() {
      this.infoOpen = !this.infoOpen;
    },
    handleScroll: _.throttle(function () {
      this.monthInTransition = true;
      if (this.scrollTimer != -1) {
        clearTimeout(this.scrollTimer);
      }
      this.scrollTimer = window.setTimeout(this.scrollFinished, 500);
    }, 100),
    scrollFinished() {
      this.monthInTransition = false;
    },
    onMouseMove() {
      this.monthInTransition = false;
      window.removeEventListener('mousemove', this.onMouseMove);
    },
    closeSubComponents() {
      this.getDatePickerMonths.forEach((month, index) => {
        const dp = this.$refs[`dp_${index}`][0];
        dp ? dp.closeYearMonthSelector() : null;
      });
    },
    close() {
      this.open = false;
      this.$emit('close');
    },
    hideBodyScroll(hide) {
      if (!this.withinModal) {
        const body = document.body;
        if (hide) {
          body.style.height = '100vh';
          body.style.overflowY = 'hidden';
        }
        else {
          body.style.height = '';
          body.style.overflowY = '';
        }
      }
    },
    updateValue() {
      const data = {
        startDate: this.value && this.value.start ? moment(this.value.start).format('D MMM YYYY') : null,
        endDate: this.value && this.value.end ? moment(this.value.end).format('D MMM YYYY') : null,
        ...this.value,
      };

      if (this.range) {
        this.$emit('input', data);
        this.$emit('selection', data);
      }
    },
    clearDates() {
      this.$emit('input', null);
      this.$emit('selection', null);
    },
    handleScreenResize(screenWidth) {
      this.checkLayout();
    },
    checkLayout() {
      if (this.isMobile && this.lazyLoadMonths) {
        this.setupInfiniteScrollingMonths();
      }
      else {
        this.setupMonths();
      }
    },
    setupMonths() {
      this.amountOfMonths = this.numberOfMonths > 0 ? this.numberOfMonths : this.double ? 2 : 1;
      this.generateMonths();
    },
    setupInfiniteScrollingMonths() {
      this.amountOfMonths = 4;
      this.generateMonths();
      this.monthsObserver ? this.monthsObserver.disconnect() : null;
      this.$nextTick(() => this.setupMonthsObserver());
    },
    setupMonthsObserver() {
      if ('IntersectionObserver' in window) {
        this.monthsObserver ? this.monthsObserver.disconnect() : null;
        let mc = [];
        if (this.$refs.monthsContainer) {
          mc = Array.from(this.$refs.monthsContainer.children);
        }
        else {
          return;
        }

        const options = {
          root: null,
          threshold: '0',
        };

        this.monthsObserver = new IntersectionObserver((entries, options) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const target = entry.target;
              if (entry.target.id !== 'invisible-load-more') {
                this.pickerMonths[entry.target.id].active = true;
              }
              // if (target.id == 'invisible-load-more') {
              //   //this.loadNewMonth();
              // }
            }
            else {
              if (entry.target.id !== 'invisible-load-more') {
                this.pickerMonths[entry.target.id].active = false;
              }
            }
          });
        });
        mc.forEach((mon) => {
          this.monthsObserver.observe(mon);
        });
      }
    },

    loadNewMonth() {
      if (this.amountOfMonths <= this.maxMonths) {
        this.monthsObserver.disconnect();
        this.amountOfMonths += 4;
        this.generateMonths();
        this.$nextTick(() => {
          this.setupMonthsObserver();
        });
      }
    },

    generateMonths() {
      const startIndex = 0;
      const lastIndex = this.amountOfMonths - 1;

      // Create array with amount of months needed
      this.pickerMonths = Array(this.amountOfMonths).fill().map((_, i) => ({
        id: i.toString(),
      }));

      let currentMonth = this.month;

      this.pickerMonths.forEach((el, index) => {
        const id = index.toString();

        const showPrev =
          !((this.limitDoubleNav
          && this.double
          && index !== startIndex
          && !this.isMobile)
          || (this.hideArrowsOnMobile && this.isMobile));

        const showNext =
          !((this.limitDoubleNav
          && this.double
          && index !== lastIndex
          && !this.isMobile)
          || (this.hideArrowsOnMobile && this.isMobile));

        Vue.set(el, 'id', id);
        Vue.set(el, 'month', currentMonth);
        Vue.set(el, 'showPrev', showPrev);
        Vue.set(el, 'showNext', showNext);
        Vue.set(el, 'show', true);
        Vue.set(el, 'active', true);

        currentMonth = this.nextMonth(currentMonth);
      });
    },

    onMouseModeChanged(val) {
      this.highlight = val;
    },

    emitValue(payload) {
      const date = payload.value;
      this.$emit('input', date);
      if (!this.range) {
        this.getTransitionName(date);
      }
    },

    // --- GETTERS ---//
    getTransitionName(date) {
      const isBigger = moment(date) > moment(`${this.date || moment().format('YYYY-MM-DD')} ${moment().format('HH:mm')}`);
      this.transitionName = isBigger ? 'slidevnext' : 'slidevprev';
    },
    getMonth(payload) {
      if (this.range || (payload && payload.start)) {
        const rangeVal = payload || this.value;
        const date = rangeVal && (rangeVal.end || rangeVal.start) ? moment(rangeVal.start) : moment();
        return new Month(date.month(), date.year());
      } if (this.value) {
        return new Month(moment(this.value, 'YYYY-MM-DD').month(), moment(this.value, 'YYYY-MM-DD').year(), this.locale);
      }
      return new Month(moment().month(), moment().year(), this.locale);
    },

    nextMonth(currentMonth) {
      const start = moment(currentMonth.start).add(1, 'months').startOf('month').format('YYYY-MM-DD');
      const end = moment(currentMonth.end).add(1, 'months').endOf('month').format('YYYY-MM-DD');
      return this.getMonth({ start, end });
    },

    changeMonth(val) {
      let month = this.month.month + (val === 'prev' ? -1 : +1);
      let year = this.month.year;
      if (month > 11 || month < 0) {
        year += (val === 'prev' ? -1 : +1);
        month = (val === 'prev' ? 11 : 0);
      }
      this.transitionDirection = val;
      this.month = new Month(month, year, this.locale);
    },
    changeYearMonth({ month, year }) {
      this.monthInTransition = true;
      window.addEventListener('mousemove', this.onMouseMove);
      this.month = new Month(month, year, this.locale);
    },
    changeYear({ month, year }, index) {
      if (index > 0) {
        this.month = new Month(this.month.month, year, this.locale);
      }
      else {
        this.month = new Month(month, year, this.locale);
      }
    },

  },
};
</script>

<style lang="scss">
@import "./assets/datepicker.scss";

  .ttt-date-picker {
    position: absolute;
    display: flex;
    z-index: 9;

    &.visible {
      z-index: 300;
    }

    .date-picker-container {
      position: absolute;
      display: flex;
      flex-direction: column;
      z-index: 5;
      overflow-x: hidden;
      background: $limeStone;
      border: 1px solid $stoneLight;
      top: -1px;

      .search-villa--sticky & {
        top: auto;
        bottom: 71px;
      }

      .months-container {
        position: relative;
        background: $limeStone;
        padding: 10px;

        @include dp-mobile-down(){
          height: 100%;
          overflow:hidden;
        }
      }

      .single{
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content:center;
        @include dp-mobile-down(){
          flex-direction: column;
          justify-content:center;
        }
      }

      .double-horizontal{
        position: relative;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content:space-around;
        @include dp-mobile-down(){
          flex-direction: column;
          flex-wrap: nowrap;
          justify-content:flex-start;
        }
      }

      .double-vertical{
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content:space-around;
        @include dp-mobile-down(){
          flex-direction: column;
          justify-content:flex-start;
        }
      }

      .date-picker-month{
        position: relative;
        overflow: visible;
      }

      // Invisible trigger or button
      .load-more{
        margin: 20px 10px 10px 10px;
        position: relative;
        font-family: $font-family-grotesk;
        color: $darkBlack;
        font-size: 18px;
        text-align: center;

        &__button{
          width:160px;
          padding:8px 30px;
          font-family: $font-family-grotesk;
          color: $darkBlack;
          font-size: 16px;
          background-color: #ffffff;
          text-align: center;
          cursor: pointer;
        }
      }

    }

    .mobile-input-container{
      position: relative;
      padding: 0px 15px 0px 10px; //on right account for 10px and 5px scrollbar
      display: block;
      background: $limeStone;
      z-index: 10;
      width:100%;
      margin:0 auto;

      &__border{
        border-top:1px solid rgb(235, 232, 232);
        @include dp-mobile-down(){
          margin: 0 20px 0px 20px;
        }
      }

      .mobile-input-weekdays{
        position: relative;
      }

      .mobile-input-wrapper{
        margin: 0px 5%;

        @include dp-mobile-down-md{
          margin: 0px 4%;
        }

        @include dp-mobile-down-sm{
          margin: 0px 3%;
          font-size: 15px;
        }

      }

      .mobile-input{
        position: relative;
        font-family: $font-family-grotesk;
        //color: #687499;
        color: $darkBlack;
        font-size: 18px;
        text-align: left;
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        margin: 10px 0 5px 0;

        .bold-text{
          font-weight: 500;
        }

        @include dp-mobile-down-sm{
          font-size: 15px;
        }

        &__icon {
          background-repeat: no-repeat;
          background-size: 18px 18px;
          width: 18px;
          height: 18px;
          cursor:pointer;

          &.calendar{
            background-image: url(./assets/icons/cal-icon-cal.svg);
          }

        }

      }

      .back-btn-container{
        position: relative;
        width:100%;
        display: flex;
        justify-content: flex-start;
        margin: 7px 0 0 0;

        .back-btn{
          position: relative;
          background: transparent;
          cursor: pointer;
          border: none;
          outline: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          text-align: left;
          height: 30px;
          width:auto;
          padding: 0;
          margin: 0 7px 0 0;
          font-family: $font-family-grotesk;
          font-size: 18px;
          color: $darkBlack;
          line-height: 1.5;

          &::before{
            position: relative;
            margin:0 6px 0 -2px;
            @include triangle(5px, $darkBlack, left);
          }
        }
      }

    }

    .bottom-container{
      margin: 0 20px;
      padding: 5px 0 5px 0;
      display: block;
      @include dp-mobile-down(){
        margin: 0 10px;
        padding: 0 0 10px 0;
        height: auto;
      }

      &__border{
        border-top:1px solid $yellowStone;
        margin: 0 40px 10px 40px;
        @include dp-mobile-down(){
          margin: 0 20px 10px 20px;
        }
      }
    }

    .calendar-info-closed {

      cursor: pointer;

      &__text{
        text-align: center;
        font-family: $font-family-grotesk;
        color: $darkBlack;
        font-size: 16px;
        font-weight: 400;
        margin: 0 0 5px 0;
      }

      &__line{
        width: 50px;
        height: 3px;
        margin: 15px auto;
        border-radius: 1.5px;
        background-color: $darkBlack;
      }
    }

    .calendar-keys{
      margin: 0 auto;
      width: 100%;
      display: grid;
      grid-template-columns: auto auto;
      align-items: center;
      justify-content:space-around;

      @include dp-mobile-size(360px){
        width:auto;
        margin: 0 0px 0 5%;
      }

      @include dp-mobile-size(288px){
        margin: 0 0px 0 2%;
      }

    }

    .calendar-keyset{
      display: grid;
      grid-column-gap: 10px;
      grid-template-columns: auto auto;
      align-items: center;
      justify-content: center;
      font-family: $font-family-grotesk;
      color: $darkBlack;
      font-size: 14px;
      font-weight: 500;
      margin: 5px 0;
      line-height: 1.1;

      &:first-child{
        padding-right: 10px;
      }
    }

    .c-key{
      width: 28px;
      height: 28px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: $darkBlack;
      font-size: 16px;
      font-weight: 400;
      font-family: $font-family-grotesk;

      &.available-date{
        background-color: $limeStone;
        border: 1px solid $darkCement;
      }

      &.contact-date{
        color: $darkBlack;
        background-color: $limeStone;
        border: 1px solid $darkCement;
        background: repeating-linear-gradient(
          45deg,
          rgba(5, 8, 23, 0.06),
          rgba(5, 8, 23, 0.06) 2px,
          rgba(255, 255, 255, 0.3) 2px,
          rgba(255, 255, 255, 0.3) 4px
        );
      }
    }

    .booking-information{
      font-family: $font-family-grotesk;
      color: $darkBlack;
      font-size: 13px;
      font-weight: 400;
      margin: 10px 0;
      text-align: center;
      text-transform: none;
      @include dp-mobile-down{
        margin: 10px 0 0 0;
      }
    }

    .button-controls-container{
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      align-items: center;
      margin:10px 0;

      .fullwidth{
        width: 100% !important;
      }

      @include dp-mobile-down{
        margin: 10px 0 0 0;
      }
    }

    .controls-container{
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .controls-btn{
      background: transparent;
      cursor: pointer;
      border: none;
      outline: none;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-family:$font-family-grotesk;
      color: $darkBlue;
      font-size: 8px;
      font-weight: 500;
    }

    .down-icon {
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      text-align: center;
      margin: 10px auto 15px auto;
      fill: $darkBlack;
    }

  }
  .inline {
    &.ttt-date-picker {
      position: relative;
      .bottom-container{
        margin: 0 0px;
        padding: 10px 0 20px 0;
        display: flex;
        flex-direction: column;
        @include dp-mobile-down(){
          padding: 0 0 20px 0;
        }

        &__border{
          border-top:1px solid rgb(235, 232, 232);
          margin: 0 20px 10px 20px;
          @include dp-mobile-down(){
            margin: 0 20px 10px 20px;
          }
        }
      }
    }
    .date-picker-container {
      position: relative;
      margin-bottom: 0 !important;
      width: 100%;
      max-width: 100%;
      background-color: transparent;
      margin: 0 auto;
    }

    .months-container {
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
      padding: 0px !important;
      @include dp-mobile-down{
        width: 100% !important;
      }
    }

    .date-picker-month{
      position: relative;
      margin:10px 0;
      overflow: visible;
    }

    .double-horizontal{
      position: relative;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content:space-between !important;
    }
  }

  @media screen and (max-width: 660px) {

  .ttt-date-picker:not(.inline) {
      position: fixed;
      margin: 0 !important;
      top: 0 !important;
      bottom: 0;
      right: 0;
      left: 0;
      overflow-x:hidden;
      z-index: 9999;

      .date-picker-container {
        position: relative;
        border-radius: 0 !important;
        margin: 0 !important;
        top: 0 !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        height: 100%;
        max-width: inherit !important;
        min-width: inherit !important;
        overflow-x: hidden;
        border: none;

        .months-container {
          align-items: center;
          height: 100%;
          overflow-x: hidden;
          overflow-y: scroll;
        }

        .date-picker-month {
          position: relative;
          margin:0px 0 20px 0;
          overflow: visible;
          width: 100%;
        }
      }
    }
  }
</style>
